/**
 * 企业微信会话票据（零依赖模块，供 request.js 与 wxauth.js 共用，避免循环引用）
 *
 * 关键原则：oauth code 是一次性凭据（只能消费一次、5 分钟过期），
 * 绝不能存下来长期复用。code 只用于换取后端的自有 token，换完立刻从地址栏抹掉。
 */

const TOKEN_KEY = 'wx_token'
// 历史遗留：老版本把 code 当票据存在这里，清理时一并删掉
const LEGACY_CODE_KEY = 'code'
const GUARD_KEY = 'wx_redirect_guard'

const CORP_ID = 'ww60a7e569d504e44f'
// 15 秒内最多允许跳这么多次授权，超过就认为进入死循环，停下来报错
const MAX_REDIRECT = 2
const REDIRECT_WINDOW = 15000

// 本次页面生命周期内是否已经在跳授权了，避免并发请求重复触发
let redirecting = false

function safeGet (storage, key) {
  try {
    return storage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSet (storage, key, value) {
  try {
    storage.setItem(key, value)
  } catch (e) {
    // 隐私模式下写不进去，忽略
  }
}

function safeRemove (storage, key) {
  try {
    storage.removeItem(key)
  } catch (e) {
    // ignore
  }
}

export function getToken () {
  return safeGet(window.localStorage, TOKEN_KEY) || ''
}

export function setToken (token) {
  if (token) {
    safeSet(window.localStorage, TOKEN_KEY, token)
    // 换到新票之后，历史遗留的死 code 就没有意义了
    safeRemove(window.localStorage, LEGACY_CODE_KEY)
  }
}

export function clearToken () {
  safeRemove(window.localStorage, TOKEN_KEY)
  safeRemove(window.localStorage, LEGACY_CODE_KEY)
}

/**
 * 从地址栏读取本次授权回跳带回来的 code
 */
export function readCode () {
  try {
    return new URLSearchParams(window.location.search).get('code') || ''
  } catch (e) {
    return ''
  }
}

/**
 * 把 code / state 从地址栏抹掉，保留业务参数（如 detail 页的 id）。
 * 不这么做的话，用户一刷新就会拿同一个 code 再换一次，必然撞上
 * 「不合法的 oauth_code」——code 只能消费一次。
 */
export function stripCode () {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('code') && !url.searchParams.has('state')) {
      return
    }
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    const search = url.searchParams.toString()
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + (search ? '?' + search : '') + url.hash
    )
  } catch (e) {
    // 不支持 replaceState 就算了，后端换票时会做单飞保护
  }
}

function readGuard () {
  try {
    return JSON.parse(safeGet(window.sessionStorage, GUARD_KEY)) || null
  } catch (e) {
    return null
  }
}

/**
 * 授权成功后清掉防循环计数
 */
export function resetRedirectGuard () {
  redirecting = false
  safeRemove(window.sessionStorage, GUARD_KEY)
}

/**
 * 跳转企业微信网页授权。
 * @returns {boolean} true 表示已发起跳转；false 表示触发了防循环保护，调用方应提示用户
 */
export function redirectToOauth () {
  if (redirecting) {
    return true
  }

  const now = Date.now()
  let guard = readGuard()
  if (!guard || now - guard.first > REDIRECT_WINDOW) {
    guard = { first: now, count: 0 }
  }
  if (guard.count >= MAX_REDIRECT) {
    return false
  }
  guard.count += 1
  safeSet(window.sessionStorage, GUARD_KEY, JSON.stringify(guard))

  redirecting = true
  window.location.replace(buildOauthUrl())
  return true
}

/**
 * 回跳地址用当前页面的真实路径 + 保留业务参数，
 * 这样从详情页失效重授权后还能回到同一张单据。
 */
function buildOauthUrl () {
  let redirectUri = window.location.origin + window.location.pathname
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    const search = url.searchParams.toString()
    redirectUri = url.origin + url.pathname + (search ? '?' + search : '')
  } catch (e) {
    // 用兜底值
  }

  const state = Math.random().toString(36).slice(2, 10)
  return 'https://open.weixin.qq.com/connect/oauth2/authorize' +
    '?appid=' + CORP_ID +
    '&redirect_uri=' + encodeURIComponent(redirectUri) +
    '&response_type=code&scope=snsapi_base&state=' + state +
    '#wechat_redirect'
}
